import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { membershipTiers } from '../data/memberships';
import { applicationInterests, referralOptions, residenceCities } from '../data/application';
import { useApplicationForm } from '../hooks/useApplicationForm';
import { DialCodeSelect } from './DialCodeSelect';
import { SelectField } from './SelectField';
import { ConsentCheckbox } from './ConsentCheckbox';
import { CityCombobox } from './CityCombobox';
const ease = [0.22, 1, 0.36, 1] as const;
const inputClass = 'w-full border-b border-ink bg-transparent pb-3 pt-1.5 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none';
const labelClass = 'block text-[13px] font-light text-ink/75';
export function ApplicationForm() {
  const f = useApplicationForm();
  if (f.status === 'sent') {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      ease
    }} role="status" className="border-t border-ink pt-10" data-id="element-43" data-mp="3bhntd1hrhitk">
        <p className="font-editorial text-[44px] leading-[48px] tracking-[-0.01em] md:text-[56px] md:leading-[60px]" data-id="element-44" data-mp="rm1c90nkiap1">
          Received, in confidence.
        </p>
        <p className="mt-5 max-w-[560px] text-[18px] font-light leading-[29px] text-ink/75" data-id="element-45" data-mp="hmu20mc4qw65">
          Thank you, {f.name.split(' ')[0]}. Your application for {f.tier} membership is now with a founding partner.
          We will call {f.dialCode} {f.phone} within 72 hours, from a private line.
        </p>
        <a href="/" className="mt-10 inline-flex items-center gap-3 text-[16px] font-medium" data-id="element-46" data-mp="wkckj6y7sd55">
          Return to the house <span aria-hidden="true" data-id="element-47" data-mp="10kwigcqm3ksc">→</span>
        </a>
      </motion.div>;
  }
  return <form onSubmit={f.submit} noValidate aria-label="Membership application" data-id="element-48" data-mp="1oaqskmjjqv8w">
      {/* Tier */}
      <fieldset data-id="element-49" data-mp="1t2inf99qjn4u">
        <legend className={labelClass} data-id="element-50" data-mp="1t30rgs2n35nb">Membership</legend>
        <div className="mt-4 grid grid-cols-1 border-y border-line sm:grid-cols-3 sm:divide-x sm:divide-line" data-id="element-51" data-mp="22xzae67d1ytu">
          {membershipTiers.map(t => {
          const active = f.tier === t.name;
          return <label key={t.name} className={`relative cursor-pointer border-b border-line px-0 py-5 transition-colors duration-200 last:border-b-0 sm:border-b-0 sm:px-6 sm:first:pl-0 ${active ? 'text-ink' : 'text-ink/45 hover:text-ink/75'}`} data-id="element-52" data-mp="1twrnekaaokf2">
                <input type="radio" name="tier" value={t.name} checked={active} onChange={() => f.setTier(t.name)} className="peer sr-only" data-id="element-53" data-mp="1f7lhkdgybvy7" />
                <span className="flex items-center justify-between gap-3 font-editorial text-[30px] leading-[34px] peer-focus-visible:underline" data-id="element-54" data-mp="1195a2kb9m1f0">
                  {t.name}
                  <span className={`flex h-4 w-4 items-center justify-center border transition-colors duration-200 ${active ? 'border-ink bg-ink text-paper' : 'border-ink/30'}`} aria-hidden="true" data-id="element-55" data-mp="wbveso172vj9">
                    {active && <CheckIcon className="h-3 w-3" strokeWidth={2.25} data-id="element-56" data-mp="112l4yhmt9d8s" />}
                  </span>
                </span>
                <span className="mt-2 block text-[14px] font-light leading-[21px]" data-id="element-57" data-mp="3fvvxntoy0df">{t.line}</span>
              </label>;
        })}
        </div>
      </fieldset>

      {/* Personal details */}
      <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-9 md:grid-cols-2" data-id="element-58" data-mp="dd0ihorg7xur">
        <Field id="apply-name" label="Full name" error={f.errors.name} data-id="element-59" data-mp="i3qs5e6wqmv9">
          <input id="apply-name" autoComplete="name" value={f.name} onChange={e => {
          f.setName(e.target.value);
          f.clear('name');
        }} placeholder="Full name" aria-invalid={!!f.errors.name} className={inputClass} data-id="element-60" data-mp="i5tpykeaxkaj" />
        </Field>
        <Field id="apply-email" label="Email" error={f.errors.email} data-id="element-61" data-mp="1mf3inx4er79i">
          <input id="apply-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" spellCheck={false} maxLength={254} value={f.email} onChange={e => {
          // Only characters that can appear in an email address
          f.setEmail(e.target.value.replace(/[^A-Za-z0-9.!#$%&'*+/=?^_`{|}~@-]/g, ''));
          f.clear('email');
        }} onBlur={f.checkEmail} placeholder="Email" aria-invalid={!!f.errors.email} className={inputClass} data-id="element-62" data-mp="2136tpkxb5956" />
        </Field>
        <Field id="apply-phone" label="Phone" error={f.errors.phone} data-id="element-63" data-mp="ppcqi4jl9w6v">
          <div className="flex items-end gap-3 border-b border-ink" data-id="element-64" data-mp="kpzuouaqpes5">
            <DialCodeSelect id="apply-code" value={f.dialCode} onChange={f.setDialCode} data-id="element-65" data-mp="2wbih8c18oijz" />
            <span className="mb-3.5 h-5 w-px bg-ink/25" aria-hidden="true" data-id="element-66" data-mp="31bek27y7a3xy" />
            <input id="apply-phone" type="tel" inputMode="tel" autoComplete="tel-national" value={f.phone} onChange={e => {
            f.setPhone(e.target.value);
            f.clear('phone');
          }} placeholder="Phone" aria-invalid={!!f.errors.phone} className="min-w-0 flex-1 bg-transparent pb-3 pt-1.5 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none" data-id="element-67" data-mp="2mlyvmym56alf" />
          </div>
        </Field>
        <Field id="apply-city" label="Home city" error={f.errors.city} data-id="element-68" data-mp="28oab5rvzl5gw">
          <CityCombobox id="apply-city" value={f.city} options={residenceCities} onChange={v => {
          f.setCity(v);
          f.clear('city');
        }} placeholder="Home city" invalid={!!f.errors.city} data-id="element-69" data-mp="1x1dy0uooul4" />
        </Field>
        {/* Keeps the thin underline of its neighbours */}
        <div className="[&>div>button]:border-b" data-id="element-70" data-mp="3rk3zt552opbc">
          <SelectField id="apply-referral" step="" label="Heard of us via (optional)" hideLabel value={f.referral} placeholder="Heard of us via (optional)" options={referralOptions} onChange={f.setReferral} data-id="element-71" data-mp="3rmp21v9a979m" />
        </div>
        <Field id="apply-referrer" label="Introduced by (optional)" data-id="element-72" data-mp="ee3rkhkxnd7t">
          <input id="apply-referrer" value={f.referrer} onChange={e => f.setReferrer(e.target.value)} placeholder="Introduced by (optional)" className={inputClass} data-id="element-73" data-mp="ebzg9ekpfbxi" />
        </Field>
      </div>

      {/* Interests */}
      <fieldset className="mt-12" data-id="element-74" data-mp="121trxstaeug6">
        <legend className={labelClass} data-id="element-75" data-mp="2kqy0ih0bi7jg">Your interests</legend>
        <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-76" data-mp="2ksr8q0qkwztk">
          {applicationInterests.map(item => {
          const on = f.interests.includes(item);
          return <button key={item} type="button" aria-pressed={on} onClick={() => f.toggleInterest(item)} className={`border px-4 py-2.5 text-[15px] transition-colors duration-200 ${on ? 'border-ink bg-ink text-paper' : 'border-ink/20 font-light text-ink hover:border-ink'}`} data-id="element-77" data-mp="2tpt6brzg9fm8">
                {item}
              </button>;
        })}
        </div>
      </fieldset>

      <div className="mt-12" data-id="element-78" data-mp="3hgfbnbi2klsj">
        <label htmlFor="apply-note" className="sr-only" data-id="element-79" data-mp="3mg1rztt72i5k">
          Anything else (optional)
        </label>
        <textarea id="apply-note" rows={3} value={f.note} onChange={e => f.setNote(e.target.value)} placeholder="Anything else (optional)" className={`${inputClass} resize-none`} data-id="element-80" data-mp="3rf4ouqz0ti2g" />
      </div>

      <div className="mt-10 flex flex-col gap-8 border-t border-line pt-8 md:flex-row md:items-start md:justify-between" data-id="element-81" data-mp="2xcmp78y7298u">
        <ConsentCheckbox id="apply-consent" checked={f.consent} onChange={v => {
        f.setConsent(v);
        f.clear('consent');
      }} error={!!f.errors.consent} className="max-w-[520px]" data-id="element-82" data-mp="3bab9ofocnedd" />
        <button type="submit" disabled={f.status === 'sending'} className="group inline-flex shrink-0 items-center justify-center gap-3 whitespace-nowrap bg-ink px-9 py-4 text-[17px] font-medium text-paper transition-opacity duration-150 hover:opacity-85 disabled:opacity-60" data-id="element-83" data-mp="2eny4amzpo3x2">
          <AnimatePresence mode="wait" initial={false} data-id="element-84" data-mp="1zve680rf4mmh">
            <motion.span key={f.status} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-85" data-mp="1vuuc76255z4k">
              {f.status === 'sending' ? 'Sealing…' : 'Submit application'}
            </motion.span>
          </AnimatePresence>
          <span aria-hidden="true" className="transition-transform duration-500 ease-luxe group-hover:translate-x-1" data-id="element-86" data-mp="1gsvkadfclmoj">
            →
          </span>
        </button>
      </div>
      {f.submitError && <p className="mt-4 text-[14px] text-[#B42318]" role="alert" data-id="element-87" data-mp="29hj92izb2u6u">
          {f.submitError}
        </p>}
    </form>;
}
type FieldProps = {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
};
function Field({
  id,
  label,
  error,
  children
}: FieldProps) {
  return <div data-id="element-88" data-mp="j53l5dwqcbeb">
      {/* The placeholder carries the visible label; this keeps it for screen readers */}
      <label htmlFor={id} className="sr-only" data-id="element-89" data-mp="o46tb780axqi">
        {label}
      </label>
      {children}
      {error && <p role="alert" className="mt-2 text-[13px] text-ink" data-id="element-90" data-mp="3myaqektijs3k">
          {error}
        </p>}
    </div>;
}